import { Component, OnInit, signal, computed, inject, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MemberOnboardingProgressService } from '../../../core/api-services/member-onboarding-progress.service';
import { MemberOnboardingProgress } from '../../../core/response-models/member-onboarding-progress.model';
import { OrganizationContextService } from '../../../core/services/organization-context.service';
import { OrganizationService } from '../../../core/data-services/organization.service';

/**
 * @deprecated Replaced by UnifiedOnboardingComponent as of 2026-02-13.
 * See: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 * This component is kept for reference but is no longer used in the application.
 * Safe to delete in a future cleanup task.
 */
@Component({
  selector: 'app-member-onboarding-banner',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './member-onboarding-banner.component.html',
  styleUrls: ['./member-onboarding-banner.component.scss']
})
export class MemberOnboardingBannerComponent implements OnInit {
  private readonly STORAGE_KEY_DISMISSED = 'adn_member_onboarding_dismissed';

  private progressService = inject(MemberOnboardingProgressService);
  private orgContext = inject(OrganizationContextService);
  private orgService = inject(OrganizationService);
  private cdr = inject(ChangeDetectorRef);

  // State signals
  progress = signal<MemberOnboardingProgress | null>(null);
  organizationName = signal<string>('Your Team');
  isLoading = signal(true);
  isDismissed = signal(false);

  // Output event to open wizard modal
  @Output() openWizard = new EventEmitter<void>();

  // Computed visibility
  isVisible = computed(() => {
    const prog = this.progress();
    const dismissed = this.isDismissed();

    // Don't show if:
    // - Still loading
    // - No progress data
    // - Onboarding complete
    // - User dismissed banner
    if (this.isLoading() || !prog || prog.completedAt !== null || dismissed) {
      return false;
    }

    return true;
  });

  // Computed remaining items
  remainingItems = computed(() => {
    const prog = this.progress();
    if (!prog) return [];

    return this.progressService.getRemainingRequiredItems(prog);
  });

  async ngOnInit(): Promise<void> {
    // Check if user previously dismissed banner
    const dismissedState = localStorage.getItem(this.STORAGE_KEY_DISMISSED);
    if (dismissedState === 'true') {
      this.isDismissed.set(true);
    }

    // Listen for restore event from sidebar
    window.addEventListener('memberOnboardingRestored', () => {
      this.isDismissed.set(false);
      localStorage.removeItem(this.STORAGE_KEY_DISMISSED);
    });

    // Load progress
    await this.loadProgress();
  }

  /**
   * Load onboarding progress from API
   */
  async loadProgress(): Promise<void> {
    try {
      this.isLoading.set(true);

      const orgId = this.orgContext.currentOrganizationId();
      if (!orgId) {
        // No organization selected, don't show banner
        this.isLoading.set(false);
        return;
      }

      // Load progress and organization name in parallel
      const [progress, organization] = await Promise.all([
        this.progressService.getProgress(orgId),
        this.orgService.getOrganization(orgId).catch(() => null)
      ]);

      this.progress.set(progress);

      // Set organization name if available
      if (organization?.name) {
        this.organizationName.set(organization.name);
      }

      // If onboarding just completed, clear dismissed state
      if (progress.completedAt) {
        this.isDismissed.set(false);
        localStorage.removeItem(this.STORAGE_KEY_DISMISSED);
      }
    } catch (error) {
      console.error('Failed to load onboarding progress:', error);
      // Don't show banner on error
    } finally {
      this.isLoading.set(false);
      // Force change detection to ensure view updates
      this.cdr.detectChanges();
    }
  }

  /**
   * Dismiss banner (hide temporarily)
   * User can re-trigger by refreshing or navigating
   */
  dismiss(): void {
    this.isDismissed.set(true);
    localStorage.setItem(this.STORAGE_KEY_DISMISSED, 'true');
    // Dispatch custom event for sidebar to show compact view
    window.dispatchEvent(new CustomEvent('memberOnboardingDismissed'));
  }

  /**
   * Open the onboarding wizard modal
   */
  completeOnboarding(): void {
    this.openWizard.emit();
  }

  /**
   * Refresh progress (called after wizard completion)
   */
  async refresh(): Promise<void> {
    await this.loadProgress();
  }
}
